import { useState, useEffect, FC } from 'react';
import Collapse from '@material-ui/core/Collapse';
import Tabs from '@material-ui/core/Tabs';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { FormProvider, useForm, FieldErrors } from 'react-hook-form';

import PaymentMethodTab from '@components/PaymentMethodTab';
import FormSectionHeader from '@components/FormSectionHeader';
import { ACTIONS, CATEGORIES } from '@constants/analytics';
import { PaymentMethod } from '@store/checkout';
import useStore from '@hooks/useStore';
import { sendEvent } from '@utils/ga';

import CardPaymentForm from './CardPaymentForm';
import CashPaymentForm from './CashPaymentForm';
import SpeiPaymentForm from './SpeiPaymentForm';
import DirectDebitPaymentForm from './DirectDebitPaymentForm';
import PayeeInfoForm from './PayeeInfoForm';
import useStyles from './styles';

import cardIcon from '@assets/images/card.svg';
import cashIcon from '@assets/images/cash.svg';
import speiIcon from '@assets/images/spei.svg';
import directDebitIcon from '@assets/images/direct-debit.svg';
import { PaymentFormProps } from './types';
import PayerCardAddressForm from './PayerCardAddressForm';
import { FormControlLabel } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import { toJS } from 'mobx';

export const getPaymentForm = (paymentMethod: PaymentMethod): FC<PaymentFormProps> => {
  switch (paymentMethod) {
    case PaymentMethod.cash:
      return CashPaymentForm;
    case PaymentMethod.card:
      return CardPaymentForm;
    case PaymentMethod.spei:
      return SpeiPaymentForm;
    case PaymentMethod.directDebit:
      return DirectDebitPaymentForm;
  }
};

const PaymentForm: FC = () => {
  const { t } = useTranslation();
  const {
    checkout,
    dlocal,
    arcus,
    fintoc,
    links: {
      paymentLink,
      coBrandingConfig: { primaryColor, secondaryColor },
      paymentConfig: {
        paymentMethods: { card },
      },
    },
  } = useStore();
  const { isOnlyCardFormRequired, isOnlyDirectDebitFormRequired, isSubscription, payeeInfo } =
    paymentLink;
  const css = useStyles({ primaryColor, secondaryColor });
  const [infoOpen, setInfoOpen] = useState(true);
  const [paymentInfoOpen, setPaymentInfoOpen] = useState(true);
  const [payerCardAddressOpen, setPayerCardAddressOpen] = useState(true);
  const [useExternalPayeeInfo, setUseExternalPayeeInfo] = useState(false);
  const methods = useForm({
    mode: 'all',
  });

  // Check if any SPEI reference is being loaded
  const isLoadingSpeiReference =
    dlocal.flags.isLoadingSpeiReference ||
    arcus.flags.isLoadingSpeiReference ||
    fintoc.flags.isLoadingSpeiReference;

  const isCardDisabled = methods.formState.isSubmitting || !checkout.isAllowedToPayWithCard;
  const isCashDisabled = methods.formState.isSubmitting || !checkout.isAllowedToPayWithCash;
  const isSpeiDisabled = methods.formState.isSubmitting || !checkout.isAllowedToPayWithSpei;
  const isDirectBebitDisabled =
    methods.formState.isSubmitting || !checkout.isAllowedToPayWithDirectDebit;

  const hasExternalPayeeInfo = Object.values(payeeInfo).some((x) => x !== '');

  // Determine which payment method to show
  // If loading SPEI reference, keep showing SPEI form
  let paymentMethod = checkout.paymentMethod;
  if (isLoadingSpeiReference) {
    paymentMethod = PaymentMethod.spei;
  } else if (!isSubscription && isCardDisabled && !isSpeiDisabled) {
    paymentMethod = PaymentMethod.spei;
  }

  const PaymentForm = getPaymentForm(paymentMethod);

  const toggleInfoSection = () => setInfoOpen(!infoOpen);
  const togglePaymentSection = () => setPaymentInfoOpen(!paymentInfoOpen);
  const toggleAddressSection = () => setPayerCardAddressOpen(!payerCardAddressOpen);

  const onTabChange = (_: React.ChangeEvent<Record<string, unknown>>, newTab: PaymentMethod) => {
    if (checkout.paymentMethod !== newTab) {
      checkout.setPaymentMethod(newTab);
    }

    if (newTab === PaymentMethod.spei) {
      setInfoOpen(false);
    }
  };

  const onValidationError = (errors: FieldErrors) => {
    const errorNames: string[] = Object.getOwnPropertyNames(errors);
    const infoFields = ['payeeFirstName', 'payeeLastName', 'payeeEmail', 'payeePhone'];
    const hasInvalidPayeeInfo = infoFields.some((fieldName) => errorNames.includes(fieldName));
    const addressFields = [
      'payerAddress',
      'payerCity',
      'payerState',
      'payerCountry',
      'payerZipCode',
    ];
    const hasInvalidPayerAddressInfo = addressFields.some((fieldName) =>
      errorNames.includes(fieldName),
    );
    setInfoOpen(hasInvalidPayeeInfo);
    setPayerCardAddressOpen(hasInvalidPayerAddressInfo);
  };

  const handleChange = (event: any) => {
    setUseExternalPayeeInfo(event.target.checked);
    if (event.target.checked) {
      const formatPhoneNumber = (phone: string) => {
        if (!phone.startsWith('+52')) {
          return `+52 ${phone.slice(0, 2)} ${phone.slice(2, 6)} ${phone.slice(6)}`;
        }
        return phone;
      };

      methods.setValue('payeePhone', formatPhoneNumber(paymentLink.payeeInfo.phone), {
        shouldDirty: true,
      });

      methods.setValue('payeeFirstName', paymentLink.payeeInfo.firstName, {
        shouldDirty: true,
      });
      methods.setValue('payeeLastName', paymentLink.payeeInfo.lastName, {
        shouldDirty: true,
      });
      methods.setValue('payeeEmail', paymentLink.payeeInfo.email, {
        shouldDirty: true,
      });
      methods.setValue('payerAddress', paymentLink.payeeInfo.street, {
        shouldDirty: true,
      });
      methods.setValue('payerCity', paymentLink.payeeInfo.city, {
        shouldDirty: true,
      });
      methods.setValue('payerState', paymentLink.payeeInfo.state, {
        shouldDirty: true,
      });
      methods.setValue('payerCountry', paymentLink.payeeInfo.country, {
        shouldDirty: true,
      });
      methods.setValue('payerZipCode', paymentLink.payeeInfo.cp, {
        shouldDirty: true,
      });
    } else {
      methods.reset();
    }
  };

  /*useEffect(() => {
    checkout.setPaymentMethod(checkout.defaultPaymentMethod);
  }, []);*/

  useEffect(() => {
    if (checkout.paymentMethod === PaymentMethod.cash) {
      sendEvent({
        category: CATEGORIES.CHECKOUT.CATEGORY,
        action: ACTIONS.CLICK,
        label: CATEGORIES.CHECKOUT.LABELS.CHECKOUT_BYLINE,
      });
    }
  }, [checkout.paymentMethod]);

  return (
    <div data-testid="payment-form" className={css.root}>
      {hasExternalPayeeInfo && (
        <div className={css.formLabelControlContainer}>
          <FormControlLabel
            label={
              <Typography className={css.formLabelControl}>
                Quiero prellenar los campos del formulario con la información de mi contrato
              </Typography>
            }
            control={
              <Checkbox
                checked={useExternalPayeeInfo}
                onChange={handleChange}
                name="externalPayee"
                color="primary"
              />
            }
          />
        </div>
      )}
      <FormProvider {...methods}>
        <form>
          <FormSectionHeader
            onClick={toggleInfoSection}
            data-testid="payment-info-trigger"
            title={t('checkoutScreen.payment.info.title')}
            isOpen={infoOpen}
          />
          <Collapse
            in={infoOpen}
            className={css.collapsible}
            timeout="auto"
            data-testid="payment-info-form-collapse"
          >
            <PayeeInfoForm />
          </Collapse>
          {card.isAddressRequired && checkout.paymentMethod === PaymentMethod.card && (
            <>
              <FormSectionHeader
                onClick={toggleAddressSection}
                data-testid="payment-payerAddress-trigger"
                title={t('checkoutScreen.payment.payerAddress.title')}
                isOpen={payerCardAddressOpen}
              />
              <Collapse
                in={payerCardAddressOpen}
                className={css.collapsible}
                timeout="auto"
                data-testid="payment-info-form-collapse"
              >
                <PayerCardAddressForm />
              </Collapse>
            </>
          )}

          {!isOnlyDirectDebitFormRequired && !isOnlyCardFormRequired ? (
            <>
              <FormSectionHeader
                onClick={togglePaymentSection}
                data-testid="payment-methods-trigger"
                title={t('checkoutScreen.payment.methods.title')}
                isOpen={paymentInfoOpen}
              />
              <Collapse
                in={paymentInfoOpen}
                className={css.collapsible}
                timeout="auto"
                data-testid="payment-method-form-collapse"
              >
                <Tabs
                  variant="fullWidth"
                  value={paymentMethod}
                  onChange={onTabChange}
                  TabIndicatorProps={{ hidden: true }}
                  className={css.paymentMethodTabs}
                >
                  {checkout.isCardAvailable && (
                    <PaymentMethodTab
                      label={t('checkoutScreen.payment.methods.card.title')}
                      icon={cardIcon}
                      value={PaymentMethod.card}
                      classes={{ root: css.tab }}
                      data-testid="payment-tab-card"
                      disabled={isCardDisabled}
                    />
                  )}
                  {checkout.isCashAvailable && !isCashDisabled && !isSubscription && (
                    <PaymentMethodTab
                      label={t('checkoutScreen.payment.methods.cash.title')}
                      icon={cashIcon}
                      value={PaymentMethod.cash}
                      classes={{ root: css.tab }}
                      data-testid="payment-tab-cash"
                      disabled={isCashDisabled}
                    />
                  )}
                  {checkout.isSpeiAvailable && !isSubscription && (
                    <PaymentMethodTab
                      label={t('checkoutScreen.payment.methods.spei.title')}
                      icon={speiIcon}
                      value={PaymentMethod.spei}
                      classes={{ root: css.tab }}
                      data-testid="payment-tab-spei"
                      disabled={isSpeiDisabled}
                    />
                  )}
                  {checkout.isDirectDebitAvailable && !isDirectBebitDisabled && isSubscription && (
                    <PaymentMethodTab
                      label={t('checkoutScreen.payment.methods.directDebit.title')}
                      icon={directDebitIcon}
                      value={PaymentMethod.directDebit}
                      classes={{ root: css.tab }}
                      disabled={isDirectBebitDisabled}
                    />
                  )}
                </Tabs>
                {!checkout.isEveryMethodDisabled && (
                  <PaymentForm onValidationError={onValidationError} />
                )}
              </Collapse>
            </>
          ) : (
            !checkout.isEveryMethodDisabled && <PaymentForm onValidationError={onValidationError} />
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default observer(PaymentForm);
